import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { useAbility } from '../../context/AbilityContext.js';
import { api } from '../../services/api.js';
import { PenaltyDialog, bookingLabel } from './PenaltyDialog.js';
import { PENALTY_TYPE_LABEL, type PenaltyList, dayWords, penaltyPowers, rm } from './penalties.js';

/**
 * 371 (#1241, FR-004): the booking page's Penalties card: the rental's penalties (from any of its legs) and "Add
 * penalty" on this booking. Shown to staff who may read penalties platform-wide (412: adding needs create); the full
 * controls (paid, paid out, cancel) live on the Penalties page.
 */
export function BookingPenaltiesCard({
  booking,
}: {
  booking: {
    id: string;
    car?: { plate_number: string } | null;
    customer?: { name: string } | null;
  };
}) {
  const [adding, setAdding] = useState(false);
  // 412: shown to anyone who may see penalties; adding needs create.
  const mayCreate = penaltyPowers(useAbility()).create;
  const list = useQuery({
    queryKey: ['penalties', 'booking', booking.id],
    queryFn: () =>
      api.get(`/penalties?booking_id=${encodeURIComponent(booking.id)}`) as Promise<PenaltyList>,
  });
  const items = list.data?.items ?? [];
  return (
    <Card id="booking-penalties" data-testid="booking-penalties">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-steel">Penalties</h2>
        {mayCreate && (
          <Button variant="secondary" onClick={() => setAdding(true)}>
            + Add penalty
          </Button>
        )}
      </div>
      {list.error && (
        <p className="text-sm text-semantic-danger-text">The penalties could not be loaded.</p>
      )}
      {list.data && items.length === 0 && <p className="text-sm text-steel">No penalty.</p>}
      {items.length > 0 && (
        <ul className="flex flex-col gap-2 text-sm text-ink">
          {items.map((p) => (
            <li key={p.id} data-status={p.status} className="flex flex-wrap items-baseline gap-x-2">
              <span
                className={p.status === 'cancelled' ? 'text-steel line-through' : 'font-medium'}
              >
                {rm(p.amount_minor)}
              </span>
              <span>{PENALTY_TYPE_LABEL[p.type] ?? p.type}</span>
              <span className="text-steel">· {dayWords(p.on)}</span>
              <span className="text-steel">· {p.reason}</span>
              <span className="text-xs text-steel">
                {p.status === 'cancelled'
                  ? '· Cancelled'
                  : `· ${p.customer_paid_on ? 'Paid' : 'Unpaid'} · CARii ${rm(p.commission.commission_minor)} · supplier payout ${rm(p.commission.payout_minor)} ${p.host_paid_out_on ? 'done' : 'pending'}`}
              </span>
            </li>
          ))}
        </ul>
      )}
      {items.length > 0 && (
        <Link
          className="mt-3 inline-block text-xs text-primary underline"
          to={`/penalties?q=${items[0]?.rental_ref ?? booking.id.slice(-8)}`}
        >
          Open in Penalties
        </Link>
      )}
      {adding && (
        <PenaltyDialog
          open
          booking={{
            id: booking.id,
            label: bookingLabel({
              id: booking.id,
              car: booking.car ?? null,
              customer: booking.customer ?? null,
            }),
          }}
          onClose={() => setAdding(false)}
        />
      )}
    </Card>
  );
}
